import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import ConfirmDialog from 'Components/Common/ConfirmDialog';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { getLandById, deleteLand } from 'services/lands';
import {
  exportProperties,
  getPropertiesByLandId,
  uploadProperties,
  removeProperty,
} from 'services/propertyService';
import CreateLandModel from './create-land-model';
import CreatePropertyModel from 'pages/Properties/create-property-model';
import { getApiErrorMessage, handleViewFile } from 'utility/utilityFunctions';
import { generateFilename } from 'utility/filenameGenerator';

import { getNavigationBreadcrumbs } from 'utility/breadcrumbUtils';
import { getFileViewUrl } from 'services/fileService';
import BulkExcelActions, {
  BulkExcelLoadingOverlay,
} from 'Components/Common/BulkExcelActions';
import { useBulkExcelOperations } from 'utility/excelBulkOperations';

const LandDetails = () => {
  const { id, organizationId, landId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(location.search);
  const mazarName = searchParams.get('mazar_name');
  const category = searchParams.get('category');
  const trustRegNumber = searchParams.get('trustRegNumber')?.trim() || '';
  const surveyNumber = searchParams.get('surveyNumber');
  const trustRegId = searchParams.get('trustRegId');
  const trustRegNumberQuery = trustRegNumber
    ? `&trustRegNumber=${encodeURIComponent(trustRegNumber)}`
    : '';
  const trustRegIdQuery = trustRegId
    ? `&trustRegId=${encodeURIComponent(trustRegId)}`
    : '';

  const [land, setLand] = useState<any>(null);
  const [properties, setProperties] = useState<any[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [propertyModal, setPropertyModal] = useState(false);
  const [propertyEditData, setPropertyEditData] = useState<any>(null);
  const [deletePropertyModal, setDeletePropertyModal] = useState(false);
  const [deletePropertyData, setDeletePropertyData] = useState<any>(null);

  const toggleEditModal = () => setEditModal(!editModal);
  const togglePropertyModal = () => {
    setPropertyModal(prev => !prev);
    if (propertyModal) setPropertyEditData(null);
  };

  const loadLand = async () => {
    dispatch(isVisible(true));
    try {
      const res = await getLandById(id || '');
      if (res?.data) setLand(res.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const loadProperties = async () => {
    try {
      const res = await getPropertiesByLandId(id || '');
      if (res?.data) setProperties(res.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e.message);
    }
  };

  useEffect(() => {
    loadLand();
    loadProperties();
  }, [id]);

  const { loading, handleExport, handleUpload } = useBulkExcelOperations({
    exportFile: () => exportProperties(Number(organizationId), Number(id)),
    getExportFilename: () =>
      generateFilename({
        organizationName: mazarName || 'Mazar',
        category: category || 'unregistered',
        trustRegNumber: trustRegNumber || 'Orphan',
        surveyNumber: surveyNumber || 'Survey',
      }),
    uploadFile: uploadProperties,
    onUploadSuccess: () => loadProperties(),
  });

  const handleDeleteProperty = async () => {
    dispatch(isVisible(true));
    try {
      await removeProperty(deletePropertyData?.id);
      toast.success('Property deleted successfully', { autoClose: 2000 });
      loadProperties();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Property delete failed'), {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
      setDeletePropertyModal(false);
      setDeletePropertyData(null);
    }
  };

  const handleDelete = async () => {
    dispatch(isVisible(true));
    try {
      await deleteLand(Number(id));
      toast.success('Land deleted');
      navigate(`/organizations/${organizationId}/lands`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  if (!land) return <div>Loading...</div>;

  const breadCrumbItems = [
    ...getNavigationBreadcrumbs({
      organizationId: organizationId || '',
      mazarName,
      category,
      trustRegNumber,
      trustRegId,
    }),

    {
      label: surveyNumber || '-',
      path: `/organizations/${organizationId}/lands/${id}?mazar_name=${mazarName}&category=${category}${trustRegNumberQuery}&surveyNumber=${surveyNumber}`,
      isActive: true,
    },
  ];

  const documentItems: { label: string; key: string; icon?: string }[] = [
    { label: 'Sanad', key: 'sanad', icon: 'ri-file-text-line' },
    {
      label: 'Registration Docs',
      key: 'registrationDocuments',
      icon: 'ri-file-list-3-line',
    },
    {
      label: 'Property Card',
      key: 'propertyCard',
      icon: 'ri-bank-card-2-line',
    },
    {
      label: 'Non-Agricultural Certificate',
      key: 'nonAgriculturalCertificate',
      icon: 'ri-file-text-line',
    },
    {
      label: 'Acquisition Document',
      key: 'acquisitionDocument',
      icon: 'ri-file-contract-line',
    },
    { label: 'Title Deed', key: 'titleDeed', icon: 'ri-file-text-line' },
    {
      label: 'Property Map / Survey Sketch',
      key: 'propertyMapSurveySketch',
      icon: 'ri-map-2-line',
    },
    {
      label: 'Title Clearance',
      key: 'titleClearance',
      icon: 'ri-shield-check-line',
    },
    {
      label: 'Payment Proof',
      key: 'paymentProof',
      icon: 'ri-money-dollar-circle-line',
    },
    {
      label: 'Sale / Gift Deed',
      key: 'saleGiftDeed',
      icon: 'ri-file-paper-2-line',
    },
    { label: 'Stamp Duty Proof', key: 'stampDutyProof', icon: 'ri-stamp-line' },
    {
      label: 'Mutation Record Proof',
      key: 'mutationRecordProof',
      icon: 'ri-file-copy-2-line',
    },
    {
      label: 'Latest Property Tax Receipt',
      key: 'latestPropertyTaxReceipt',
      icon: 'ri-receipt-line',
    },

    // Additional Land Documents
    {
      label: 'Survey Number Records',
      key: 'surveyNumberRecords',
      icon: 'ri-file-text-line',
    },
    {
      label: 'Mutation Entries (ROR)',
      key: 'mutationEntriesROR',
      icon: 'ri-file-copy-2-line',
    },
    {
      label: 'Old Ownership Documents',
      key: 'oldOwnershipDocuments',
      icon: 'ri-file-history-line',
    },
    {
      label: 'Title Search Report',
      key: 'titleSearchReport',
      icon: 'ri-search-2-line',
    },
    {
      label: 'Government Allotment Letters',
      key: 'governmentAllotmentLetters',
      icon: 'ri-government-line',
    },
    { label: '135-D Notice', key: 'notice135D', icon: 'ri-file-warning-line' },
    {
      label: 'Village Form 7 (7/12 Extract)',
      key: 'villageForm7',
      icon: 'ri-file-list-2-line',
    },
    {
      label: 'Village Form 8A',
      key: 'villageForm8A',
      icon: 'ri-file-list-2-line',
    },
    {
      label: 'Form No. 6 (Haq Patraka)',
      key: 'form6HaqPatraka',
      icon: 'ri-file-list-3-line',
    },
    {
      label: 'Form No. 6 (Haq Patraka Nondh)',
      key: 'form6HaqPatrakaNondh',
      icon: 'ri-file-list-3-line',
    },
    { label: '52/53 Utara', key: 'utara5253', icon: 'ri-file-text-line' },
    {
      label: 'Talati Map (Half/Full)',
      key: 'talatiMap',
      icon: 'ri-map-pin-line',
    },
    {
      label: 'Collector Order / N.A. Order',
      key: 'collectorOrderNAOrder',
      icon: 'ri-auction-line',
    },
    { label: 'Lease Deeds', key: 'leaseDeeds', icon: 'ri-file-paper-2-line' },
    {
      label: 'Encumbrance Certificate',
      key: 'encumbranceCertificate',
      icon: 'ri-shield-star-line',
    },
  ];

  return (
    <React.Fragment>
      {loading && <BulkExcelLoadingOverlay />}
      <div
        className="page-content"
        style={{ maxHeight: 'calc(100vh - 20px)', overflowY: 'auto' }}
      >
        <ToastContainer closeButton={false} limit={1} />
        <ConfirmDialog
          isOpen={deleteModal}
          title="Confirm Delete"
          message="Are you sure you want to delete this land?"
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteModal(false)}
        />
        <ConfirmDialog
          isOpen={deletePropertyModal}
          title="Delete Property"
          message={`Are you sure you want to delete "${deletePropertyData?.name || 'this property'}"?`}
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteProperty}
          onCancel={() => {
            setDeletePropertyModal(false);
            setDeletePropertyData(null);
          }}
        />
        <Container fluid>
          <BreadCrumb
            title={`Survey ${land.surveyNumber}`}
            breadcrumbItems={breadCrumbItems}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">
                      Survey {land.surveyNumber}
                    </h4>
                    <div className="d-flex gap-2">
                      {category === 'unregistered' && (
                        <Button color="primary" onClick={() => null}>
                          <i className="ri-add-line align-bottom me-1"></i> Add
                          Registration
                        </Button>
                      )}
                      <Button color="primary" onClick={toggleEditModal}>
                        <i className="ri-edit-line align-bottom me-1"></i> Edit
                        Land
                      </Button>
                      <Button
                        color="danger"
                        onClick={() => setDeleteModal(true)}
                      >
                        <i className="ri-delete-bin-line align-bottom me-1"></i>{' '}
                        Delete Land
                      </Button>
                    </div>
                  </div>

                  {/* Land Information */}
                  <Row className="mb-4 g-3">
                    <Col lg={6}>
                      <Card className="shadow-sm border-0 h-100">
                        <CardBody>
                          <h5 className="mb-3">
                            <i className="ri-information-line align-middle me-1 text-primary"></i>
                            Basic Information
                          </h5>
                          <table className="table table-sm mb-0">
                            <tbody>
                              <tr>
                                <th style={{ width: '220px' }}>
                                  Property Address
                                </th>
                                <td>{land.propertyAddress}</td>
                              </tr>
                              <tr>
                                <th>State</th>
                                <td>{land.state}</td>
                              </tr>
                              <tr>
                                <th>City/District</th>
                                <td>{land.cityDistrict}</td>
                              </tr>
                              <tr>
                                <th>Pincode</th>
                                <td>{land.pincode}</td>
                              </tr>
                              <tr>
                                <th>Area (Sq. Mt.)</th>
                                <td>{land.areaSqMt}</td>
                              </tr>
                              <tr>
                                <th>Geo Coordinates</th>
                                <td>{land.geoCoordinates || '-'}</td>
                              </tr>
                              <tr>
                                <th>Ward No.</th>
                                <td>{land.wardNo || '-'}</td>
                              </tr>
                              <tr>
                                <th>Sheet No.</th>
                                <td>{land.sheetNo || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col lg={6}>
                      <Card className="shadow-sm border-0 h-100 bg-light-subtle">
                        <CardBody>
                          <h5 className="mb-3">
                            <i className="ri-list-check-2 align-middle me-1 text-primary"></i>
                            Survey & Acquisition
                          </h5>
                          <table className="table table-sm mb-0">
                            <tbody>
                              <tr>
                                <th style={{ width: '220px' }}>
                                  Survey Number
                                </th>
                                <td>{land.surveyNumber}</td>
                              </tr>
                              <tr>
                                <th>Khasra</th>
                                <td>{land.khasra || '-'}</td>
                              </tr>
                              <tr>
                                <th>Plot Number</th>
                                <td>{land.plotNumber || '-'}</td>
                              </tr>
                              <tr>
                                <th>Registration Number</th>
                                <td>{land.registrationNumber}</td>
                              </tr>
                              <tr>
                                <th>Registration Date</th>
                                <td>{land.registrationDate}</td>
                              </tr>
                              <tr>
                                <th>Type of Acquisition</th>
                                <td>{land.typeOfAcquisition || '-'}</td>
                              </tr>
                              <tr>
                                <th>Date of Acquisition</th>
                                <td>{land.dateOfAcquisition || '-'}</td>
                              </tr>
                              <tr>
                                <th>Seller/Donor</th>
                                <td>{land.sellerDonorName || '-'}</td>
                              </tr>
                              <tr>
                                <th>Cost of Acquisition</th>
                                <td>{land.costOfAcquisition || '-'}</td>
                              </tr>
                              <tr>
                                <th>Payment Method</th>
                                <td>{land.paymentMethod || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  {/* Documents */}
                  <h5 className="mb-3 mt-3">Documents & Images</h5>
                  <Row className="g-3 mb-4">
                    {documentItems.map((doc, idx) => {
                      const rawVal = land?.[doc.key];
                      const links = rawVal
                        ? String(rawVal).split(',').filter(Boolean)
                        : [];
                      return (
                        <Col xs={6} md={3} key={idx}>
                          <Card className="border-0 shadow-sm h-100 text-center hover-shadow">
                            <CardBody className="py-4">
                              <i
                                className={`${doc.icon || 'ri-file-2-line'} fs-32 text-primary mb-2`}
                              />
                              <h6 className="fs-13 fw-semibold mb-2">
                                {doc.label}
                              </h6>
                              {links.length ? (
                                <>
                                  {links.map((l: string, i: number) => (
                                    <Button
                                      key={i}
                                      size="sm"
                                      color="primary"
                                      className="me-1 mb-1"
                                      onClick={() => handleViewFile(l)}
                                    >
                                      {links.length > 1
                                        ? `File ${i + 1}`
                                        : 'View'}
                                    </Button>
                                  ))}
                                </>
                              ) : (
                                <span className="badge bg-warning-subtle text-warning">
                                  Not uploaded
                                </span>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>

                  {/* Land Photos */}
                  <h6 className="mb-3">Land Photos</h6>
                  {land.landPhotos ? (
                    <Row>
                      {land.landPhotos
                        .split(',')
                        .map((p: string, idx: number) => (
                          <Col key={idx} lg={3} md={4} sm={6} className="mb-3">
                            <div className="position-relative">
                              <img
                                src={getFileViewUrl(p)}
                                alt={`Land Photo ${idx + 1}`}
                                className="img-fluid rounded"
                                style={{
                                  height: '200px',
                                  width: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                              <Button
                                color="primary"
                                size="sm"
                                className="position-absolute top-0 end-0 m-2"
                                onClick={() => handleViewFile(p)}
                              >
                                <i className="ri-eye-line"></i>
                              </Button>
                            </div>
                          </Col>
                        ))}
                    </Row>
                  ) : (
                    <p className="text-muted">No photos uploaded.</p>
                  )}

                  {/* Properties List Header with button */}
                  <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                    <h5 className="mb-0">Properties on this Land</h5>
                    <Button
                      color="primary"
                      onClick={() => setPropertyModal(true)}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add
                      Property
                    </Button>
                  </div>

                  <BulkExcelActions
                    helpTargetId="land-details-bulk-upload-help"
                    onDownload={handleExport}
                    onUpload={handleUpload}
                    containerClassName="d-flex flex-column align-items-end"
                    disabled={loading}
                    downloadDisabled={loading || properties.length < 1}
                  />
                  {/* Properties List */}
                  <Row className="g-3">
                    {properties.length ? (
                      properties.map(prop => (
                        <Col lg={4} md={6} key={prop.id}>
                          <Card className="border-0 shadow-sm h-100 position-relative hover-shadow">
                            <UncontrolledDropdown className="position-absolute end-0 me-2 mt-2">
                              <DropdownToggle
                                tag="span"
                                className="cursor-pointer text-muted"
                              >
                                <i className="ri-more-2-fill" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem
                                  onClick={() => {
                                    setPropertyEditData(prop);
                                    setPropertyModal(true);
                                  }}
                                >
                                  <i className="ri-edit-line me-2"></i> Edit
                                </DropdownItem>
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => {
                                    setDeletePropertyData(prop);
                                    setDeletePropertyModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line me-2"></i>{' '}
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                            <CardBody>
                              <h5
                                className="fs-16 fw-semibold mb-1 cursor-pointer text-truncate"
                                onClick={() =>
                                  navigate(
                                    `/properties/${prop.id}?mazar_name=${encodeURIComponent(mazarName || '')}&property_name=${encodeURIComponent(prop.name)}&mazar_id=${organizationId}${trustRegIdQuery}&surveyNumber=${land?.surveyNumber || surveyNumber}${trustRegNumberQuery}&landId=${id}&category=${category}`,
                                  )
                                }
                              >
                                {prop.name}
                              </h5>
                              <p
                                className="text-muted small mb-0 text-truncate"
                                title={prop.address}
                              >
                                Address: {prop.address}
                              </p>
                              <p className="text-muted small mb-0 text-truncate">
                                Size: {prop.size || '-'}
                              </p>
                            </CardBody>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <Col>
                        <div className="text-center py-5">
                          <h4>No properties available</h4>
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <CreateLandModel
          isOpen={editModal}
          toggle={toggleEditModal}
          organizationId={Number(organizationId)}
          organizationName={mazarName || ''}
          trustRegId={land.registrationId || null}
          type={category}
          onSuccess={() => {
            loadLand();
            loadProperties();
          }}
          editData={land}
        />
        <CreatePropertyModel
          isOpen={propertyModal}
          toggle={togglePropertyModal}
          organizationId={land.organizationId}
          organizationName={mazarName || ''}
          landId={land.id || id}
          onSuccess={() => {
            loadProperties();
          }}
          editData={propertyEditData}
        />
      </div>
    </React.Fragment>
  );
};

export default LandDetails;
